import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import { Button } from "@/components/ui/button";
import { Download, Trash2, Info, FileText, Map } from "lucide-react";
import { useLanguage } from '@/contexts/LanguageContext';
import AddressSearch from './AddressSearch';

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Utility function to calculate polygon area in square meters
const calculatePolygonArea = (latlngs: L.LatLng[]): number => {
  if (latlngs.length < 3) return 0;
  let area = 0;
  const R = 6371000; // Earth's radius in meters

  for (let i = 0; i < latlngs.length; i++) {
    const j = (i + 1) % latlngs.length;
    const lat1 = latlngs[i].lat * Math.PI / 180;
    const lat2 = latlngs[j].lat * Math.PI / 180;
    const deltaLng = (latlngs[j].lng - latlngs[i].lng) * Math.PI / 180;
    area += deltaLng * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  area = Math.abs(area * R * R / 2);
  return area;
};
const formatArea = (area: number): string => {
  if (area >= 1000000) {
    return `${(area / 1000000).toFixed(2)} km²`;
  }
  return `${area.toFixed(0)} m²`;
};
interface KMLGeneratorProps {
  mode?: 'generator' | 'viewer';
  onModeChange?: (mode: 'generator' | 'viewer') => void;
  showModeToggle?: boolean;
  isEmbedded?: boolean;
}
const KMLGenerator = ({
  mode = 'generator',
  onModeChange,
  showModeToggle = false,
  isEmbedded = false
}: KMLGeneratorProps) => {
  const {
    t
  } = useLanguage();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const drawnItems = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const addressMarker = useRef<L.Marker | null>(null);
  const polygonDrawer = useRef<any>(null);
  const [hasDrawing, setHasDrawing] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);
  const [totalArea, setTotalArea] = useState(0);

  // Centralized cursor management - only crosshair and grab
  const updateCursor = (isDragMode: boolean = false) => {
    if (!mapInstance.current) return;
    const mapContainer = mapInstance.current.getContainer();
    mapContainer.style.cursor = isDragMode ? 'grab' : 'crosshair';
  };

  // Calculate total area of all drawn polygons
  const updateTotalArea = () => {
    const layers = drawnItems.current.getLayers();
    let total = 0;
    layers.forEach((layer: any) => {
      if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        total += calculatePolygonArea(latlngs);
      }
    });
    setTotalArea(total);
  };
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    // Initialize map - always start in draw mode, centered on Berlin
    const map = L.map(mapRef.current, {
      center: [52.5200, 13.4050], // Berlin, Germany coordinates
      zoom: 10,
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      touchZoom: true,
      boxZoom: false,
      dragging: false
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    // Add drawn items layer
    map.addLayer(drawnItems.current);

    // Initialize polygon drawer with smaller corner points
    polygonDrawer.current = new L.Draw.Polygon(map, {
      shapeOptions: {
        color: '#2563eb',
        fillColor: '#3b82f6',
        fillOpacity: 0.3,
        weight: 3
      },
      icon: new L.DivIcon({
        iconSize: new L.Point(6, 6),
        // Smaller corner points
        className: 'leaflet-div-icon leaflet-editing-icon',
        html: '<span role="button" aria-label="Drawing point"></span>'
      })
    });

    // Add accessibility labels to drawing markers
    const addAccessibilityToMarkers = () => {
      // Add aria-label to mouse marker (drawing cursor)
      const mouseMarkers = mapContainer.querySelectorAll('.leaflet-mouse-marker');
      mouseMarkers.forEach((marker) => {
        if (marker.getAttribute('role') === 'button' && !marker.getAttribute('aria-label')) {
          marker.setAttribute('aria-label', 'Drawing cursor - click to place point');
        }
      });

      // Add aria-label to editing icons (corner points)
      const editingIcons = mapContainer.querySelectorAll('.leaflet-editing-icon');
      editingIcons.forEach((icon) => {
        if (icon.getAttribute('role') === 'button' && !icon.getAttribute('aria-label')) {
          icon.setAttribute('aria-label', 'Polygon corner point');
        }
      });

      // Add aria-label to any other interactive map markers
      const interactiveMarkers = mapContainer.querySelectorAll('[role="button"][tabindex="0"]');
      interactiveMarkers.forEach((marker) => {
        if (!marker.getAttribute('aria-label') && !marker.textContent?.trim()) {
          marker.setAttribute('aria-label', 'Map marker');
        }
      });
    };

    // Run accessibility fixes periodically during drawing
    const accessibilityInterval = setInterval(addAccessibilityToMarkers, 500);

    // Start in draw mode
    polygonDrawer.current.enable();

    // Set crosshair cursor immediately and ensure it's applied
    const mapContainer = map.getContainer();
    mapContainer.style.cursor = 'crosshair';
    
    // Force cursor update after a brief delay to override any default behaviors
    setTimeout(() => {
      mapContainer.style.cursor = 'crosshair';
    }, 100);

    // Event handlers
    map.on(L.Draw.Event.DRAWSTART, () => {
      setIsDrawing(true);
    });
    map.on(L.Draw.Event.CREATED, (event: any) => {
      const layer = event.layer;
      drawnItems.current.addLayer(layer);
      setHasDrawing(true);
      setIsDrawing(false);
      updateTotalArea();
    });
    
    mapInstance.current = map;
    return () => {
      clearInterval(accessibilityInterval);
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  // Handle spacebar for temporary mode switching
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent spacebar action if user is typing in an input field
      const activeElement = document.activeElement;
      const isInputField = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.getAttribute('contenteditable') === 'true');
      if (e.code === 'Space' && !spacePressed && !isInputField) {
        e.preventDefault();
        setSpacePressed(true);

        // Switch to temporary drag mode
        if (mapInstance.current) {
          updateCursor(true); // Set grab cursor
          if (polygonDrawer.current) {
            polygonDrawer.current.disable();
          }
          mapInstance.current.dragging.enable();
        }
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      // Prevent spacebar action if user is typing in an input field
      const activeElement = document.activeElement;
      const isInputField = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.getAttribute('contenteditable') === 'true');
      if (e.code === 'Space' && spacePressed && !isInputField) {
        e.preventDefault();
        setSpacePressed(false);

        // Return to draw mode
        if (mapInstance.current) {
          updateCursor(false); // Set crosshair cursor
          if (polygonDrawer.current) {
            polygonDrawer.current.enable();
          }
          mapInstance.current.dragging.disable();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [spacePressed]);
  
  // Handle location found
  const handleLocationFound = (lat: number, lng: number, address: string) => {
    if (mapInstance.current) {
      mapInstance.current.setView([lat, lng], 15);

      // Remove existing address marker
      if (addressMarker.current) {
        mapInstance.current.removeLayer(addressMarker.current);
      }

      // Add new address marker with accessible label
      addressMarker.current = L.marker([lat, lng]).addTo(mapInstance.current);
      addressMarker.current.bindPopup(address).openPopup();

      // Add accessibility to the marker element
      setTimeout(() => {
        const markerElements = mapInstance.current?.getContainer().querySelectorAll('.leaflet-marker-icon');
        markerElements?.forEach((markerEl) => {
          if (!markerEl.getAttribute('aria-label')) {
            markerEl.setAttribute('aria-label', `Location marker: ${address}`);
          }
        });
      }, 100);

      // Show success notification
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10b981;
        color: white;
        padding: 12px 16px;
        border-radius: 6px;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        font-size: 14px;
        font-weight: 500;
        max-width: 300px;
      `;
      notification.textContent = `${t('addressSearch.found')}: ${address}`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);
    }
  };
  
  // Generate KML
  const generateKML = () => {
    if (!hasDrawing) {
      return;
    }
    const layers = drawnItems.current.getLayers();
    let kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Generated Areas</name>
    <description>Areas drawn using KML Generator</description>
    <Style id="AreaStyle">
      <LineStyle>
        <color>ff1e69d4</color>
        <width>3</width>
      </LineStyle>
      <PolyStyle>
        <color>4d1e69d4</color>
      </PolyStyle>
    </Style>
`;
    layers.forEach((layer: any, index: number) => {
      let coordinates = '';
      if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
        const latlngs = layer.getLatLngs()[0] as L.LatLng[];
        coordinates = latlngs.map((latlng: L.LatLng) => `${latlng.lng},${latlng.lat},0`).join(' ');
        // Close the polygon
        if (latlngs.length > 0) {
          coordinates += ` ${latlngs[0].lng},${latlngs[0].lat},0`;
        }
        kmlContent += `
    <Placemark>
      <name>Area ${index + 1}</name>
      <description>Drawn area ${index + 1}</description>
      <styleUrl>#AreaStyle</styleUrl>
      <Polygon>
        <extrude>1</extrude>
        <altitudeMode>clampToGround</altitudeMode>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>${coordinates}</coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>`;
      }
    });
    kmlContent += `
  </Document>
</kml>`;

    // Create and download the file
    const blob = new Blob([kmlContent], {
      type: 'application/vnd.google-earth.kml+xml'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `areas_${new Date().toISOString().split('T')[0]}.kml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Clear all drawn items
  const clearAll = () => {
    drawnItems.current.clearLayers();
    setHasDrawing(false);
    setTotalArea(0);
  };
  
  return <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">{t('header.title')}</h1>
            <p className="text-blue-100">{t('header.description')}</p>
          </div>
          {onModeChange && <Button onClick={() => onModeChange('viewer')} variant="outline" size="sm" className="flex items-center gap-2 border-white hover:bg-white text-blue-950">
              <FileText className="h-4 w-4" />
             KML File Viewer
            </Button>}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">{t('instructions.title')}</p>
            <ol className="list-decimal list-inside space-y-1">
              
              <li>{t('instructions.step2')}</li>
              <li>{t('instructions.step3')}</li>
              <li>{t('instructions.step4')}</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Address Search */}
      <div className="p-4 border-b bg-gray-50">
        <AddressSearch onLocationFound={handleLocationFound} />
      </div>

      {/* Map Container */}
      <div className="relative">
        <div ref={mapRef} className="w-full h-96 bg-gray-200 kml-map-container" style={{
        minHeight: '400px'
      }} />

        {/* Status Indicators */}
        {isDrawing && <div className="absolute top-4 right-4 bg-yellow-100 border border-yellow-400 text-yellow-700 px-3 py-2 rounded-lg shadow-lg">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse mr-2"></div>
              {t('map.drawing')}
            </div>
          </div>}

        {spacePressed && <div className="absolute top-4 left-4 bg-green-100 border border-green-400 text-green-700 px-3 py-2 rounded-lg shadow-lg">
            <div className="flex items-center text-sm">
              {t('map.dragMode')}
            </div>
          </div>}
      </div>

      {/* Action Buttons */}
      <div className="p-4 bg-gray-50 border-t">
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <div className="text-sm text-gray-600">
            {hasDrawing ? (
              <span>
                {drawnItems.current.getLayers().length} {t('status.areasDrawn')}
                {totalArea > 0 && (
                  <span className="ml-2 font-medium">
                    • Total Area: {formatArea(totalArea)}
                  </span>
                )}
              </span>
            ) : (
              t('status.noAreas')
            )}
          </div>
          
          <div className="flex gap-3">
            <Button onClick={clearAll} variant="outline" size="sm" disabled={!hasDrawing} className="flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              {t('actions.clearAll')}
            </Button>
            
            <Button onClick={generateKML} disabled={!hasDrawing} size="sm" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
              <Download className="h-4 w-4" />
              {t('actions.download')}
            </Button>
          </div>
        </div>
      </div>

      {/* Footer - only show when not embedded */}
      {!isEmbedded && (
        <div className="px-4 py-3 bg-gray-100 text-xs text-gray-500 text-center">
          {t('footer.description')}
        </div>
      )}
    </div>;
};
export default KMLGenerator;
